import { Outlet, createRootRouteWithContext, createRoute, redirect } from '@tanstack/react-router';
import type { QueryClient } from '@tanstack/react-query';
import { AppLayout } from '@/app/layouts/AppLayout';
import { AuthLayout } from '@/app/layouts/AuthLayout';
import { NotFoundPage } from '@/app/routes/NotFoundPage';
import { isSignedIn } from '@/lib/session';

/**
 * Router context — available to every route's `beforeLoad` and `loader`.
 *
 * The query client is passed in (rather than imported) so route loaders can
 * prefetch into the same cache the components read from.
 */
export interface RouterContext {
  queryClient: QueryClient;
}

export const rootRoute = createRootRouteWithContext<RouterContext>()({
  component: Outlet,
  notFoundComponent: NotFoundPage,
});

/**
 * Pathless layout route for signed-out screens.
 *
 * Pathless (`id`, no `path`) so the URLs stay clean — `/login`, not
 * `/auth/login`.
 */
export const authLayoutRoute = createRoute({
  getParentRoute: () => rootRoute,
  id: '_auth',
  component: AuthLayout,
  beforeLoad: () => {
    // An already-signed-in user landing on /login should go to the app, not
    // sign in a second time.
    if (isSignedIn()) {
      throw redirect({ to: '/' });
    }
  },
});

/**
 * Pathless layout route for everything behind authentication.
 *
 * Guarding here rather than per-route means a new module cannot accidentally
 * ship unguarded: attaching to this parent is what gives a route the app shell,
 * so forgetting the guard would also mean forgetting the layout, which is
 * immediately visible.
 */
export const appLayoutRoute = createRoute({
  getParentRoute: () => rootRoute,
  id: '_app',
  component: AppLayout,
  beforeLoad: ({ location }) => {
    if (!isSignedIn()) {
      throw redirect({
        to: '/login',
        // Preserve where they were headed so sign-in can return them there.
        search: { next: location.href },
      });
    }
  },
});
